import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { ProductType } from "@/models/product";

type Props = {
    open: boolean;
    category: ProductType;
    onConfirm: () => void;
    onClose: () => void;
};

export function ReplaceProductDialog({
    open,
    category,
    onConfirm,
    onClose,
}: Props) {
    const { t } = useTranslation();
    const ref = useRef<HTMLDialogElement>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);
    const previouslyFocused = useRef<Element | null>(null);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        if (open) {
            previouslyFocused.current = document.activeElement;
            if (!node.open) node.showModal();
            cancelRef.current?.focus();
        } else if (node.open) {
            node.close();
            const prev = previouslyFocused.current;
            if (prev instanceof HTMLElement) prev.focus();
        }
    }, [open]);

    const subtitleKey =
        category === "SUN_PROTECTION"
            ? "replaceProductDialog.subtitleSunProtection"
            : "replaceProductDialog.subtitleWindowDecoration";

    return (
        <dialog
            ref={ref}
            data-testid="replace-product-dialog"
            aria-labelledby="replace-product-dialog-title"
            onCancel={(event) => {
                event.preventDefault();
                onClose();
            }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl p-0 shadow-xl backdrop:bg-black/40"
        >
            <div className="w-[min(420px,90vw)] p-6 flex flex-col gap-4">
                <h2
                    id="replace-product-dialog-title"
                    className="text-xl font-bold text-gray-900"
                >
                    {t("replaceProductDialog.title")}
                </h2>
                <p className="text-sm text-gray-600">{t(subtitleKey)}</p>
                <div className="mt-2 flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={onConfirm}
                        className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-contrast-text hover:bg-primary-dark cursor-pointer"
                    >
                        {t("replaceProductDialog.confirm")}
                    </button>
                    <button
                        ref={cancelRef}
                        type="button"
                        onClick={onClose}
                        className="rounded-full border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer"
                    >
                        {t("replaceProductDialog.cancel")}
                    </button>
                </div>
            </div>
        </dialog>
    );
}
